/**
 * External Accountant Workspace — Iter-109
 *
 * Command Center for chartered accountants, external auditors,
 * and tax/zakat authorities. Single pane of glass that aggregates:
 *   • Compliance KPI Bar (top)
 *   • External Accountant Inbox (today's attention items)
 *   • 11 navigation cards to underlying modules
 *
 * Backed by GET /api/accountant-workspace/summary
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  ShieldCheck, FileCheck2, Calculator, Building2, Scale, BookOpen,
  FileBarChart, Briefcase, Lock, Receipt, Archive, FolderArchive,
  AlertTriangle, Inbox as InboxIcon, ArrowLeft, RefreshCw, Activity,
} from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.round(n || 0));

const StatusPill = ({ status }) => {
  const map = {
    compliant: { c: "emerald", l: "متوافق" },
    pending:   { c: "amber",   l: "بانتظار الإجراء" },
    overdue:   { c: "rose",    l: "متأخر" },
    no_returns:{ c: "slate",   l: "لا توجد" },
    open:      { c: "amber",   l: "مفتوحة" },
    closed:    { c: "emerald", l: "مغلقة" },
    locked:    { c: "violet",  l: "مقفلة" },
  };
  const m = map[status] || { c: "slate", l: status || "—" };
  return (
    <span className={`inline-flex items-center rounded-full bg-${m.c}-100 px-2.5 py-0.5 text-xs font-semibold text-${m.c}-700`}>
      {m.l}
    </span>
  );
};

const KpiTile = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

const ScoreGauge = ({ score }) => {
  const tone = score >= 85 ? "emerald" : score >= 65 ? "amber" : "rose";
  return (
    <div data-testid="kpi-compliance-score" className={`rounded-2xl border-2 border-${tone}-200 bg-gradient-to-br from-${tone}-50 to-white p-4 shadow-sm`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">درجة الامتثال</div>
      <div className={`mt-1 text-4xl font-bold text-${tone}-600`}>{score}<span className="text-lg text-slate-400">/100</span></div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full bg-${tone}-500 transition-all`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

const InboxRow = ({ icon: Icon, label, count, severity, onClick, testid }) => {
  const hot = count > 0;
  const tone = !hot ? "slate" : severity === "danger" ? "rose" : severity === "warn" ? "amber" : "violet";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`group flex w-full items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-right transition-all hover:border-${tone}-400 hover:shadow-md`}
    >
      <div className="flex items-center gap-3">
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-${tone}-100 text-${tone}-600`}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className="text-sm font-semibold text-slate-900">{label}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`min-w-[2.5rem] rounded-full px-2.5 py-1 text-center text-sm font-bold ${hot ? `bg-${tone}-100 text-${tone}-700` : "bg-slate-100 text-slate-500"}`}>
          {count}
        </span>
        <ArrowLeft className="h-4 w-4 text-slate-400 transition-transform group-hover:-translate-x-1" />
      </div>
    </button>
  );
};

const ModuleCard = ({ icon: Icon, title, kpi, subtitle, link, color = "violet", testid }) => {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testid}
      onClick={() => navigate(link)}
      className={`group rounded-2xl border border-slate-200 bg-white p-5 text-right shadow-sm transition-all hover:-translate-y-0.5 hover:border-${color}-400 hover:shadow-lg`}
    >
      <div className="flex items-start justify-between">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-${color}-100 text-${color}-600`}>
          <Icon className="h-5 w-5" />
        </span>
        <ArrowLeft className="h-4 w-4 text-slate-400 transition-transform group-hover:-translate-x-1" />
      </div>
      <div className="mt-4 text-base font-bold text-slate-900">{title}</div>
      <div className={`mt-1 text-lg font-bold text-${color}-700`}>{kpi}</div>
      <div className="mt-0.5 text-xs text-slate-500">{subtitle}</div>
    </button>
  );
};

const MODULE_DEFS = [
  { key: "vat",                  title: "VAT Center",              link: "/vat-return",         icon: Calculator,    color: "emerald" },
  { key: "zatca",                title: "ZATCA Center",            link: "/zatca",              icon: Building2,     color: "violet"  },
  { key: "trial_balance",        title: "Trial Balance",           link: "/accounting-reports", icon: Scale,         color: "indigo"  },
  { key: "general_ledger",       title: "General Ledger",          link: "/journal",            icon: BookOpen,      color: "sky"     },
  { key: "financial_statements", title: "Financial Statements",    link: "/accounting-reports", icon: FileBarChart,  color: "emerald" },
  { key: "audit_packages",       title: "Audit Packages",          link: "/audit-trail",        icon: ShieldCheck,   color: "violet"  },
  { key: "cfo_packs",            title: "CFO Packs",               link: "/cfo-pack",           icon: Briefcase,     color: "amber"   },
  { key: "month_end",            title: "Month-End Closing",       link: "/month-end",          icon: Lock,          color: "rose"    },
  { key: "tax_adjustments",      title: "Tax Adjustments",         link: "/external-accountant/tax-adjustments", icon: Receipt,       color: "amber"   },
  { key: "government_filings",   title: "Government Filings",      link: "/archive-center",     icon: Archive,       color: "indigo"  },
  { key: "evidence_vault",       title: "Evidence Vault",          link: "/documents",          icon: FolderArchive, color: "slate"   },
];

export default function ExternalAccountantWorkspace() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const r = await axiosInstance.get("/accountant-workspace/summary");
      setData(r.data);
    } catch (e) {
      toast.error("تعذّر تحميل بيانات الـ Workspace");
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axiosInstance.get("/accountant-workspace/summary");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error("تعذّر تحميل بيانات الـ Workspace");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) {
    return (
      <div data-testid="accountant-workspace-loading" className="p-8 text-center text-slate-500">
        <Activity className="mx-auto h-6 w-6 animate-pulse" />
        <div className="mt-2 text-sm">جارٍ تحضير لوحة المحاسب القانوني…</div>
      </div>
    );
  }

  const kpi = data.kpi_bar;
  const inbox = data.inbox;
  const cards = data.cards || {};

  const totalInboxCount =
    (inbox.outstanding_vat_issues?.count || 0) +
    (inbox.missing_documents?.count || 0) +
    (inbox.unposted_journals?.count || 0) +
    (inbox.unreconciled_accounts?.count || 0) +
    (inbox.month_end_warnings?.count || 0) +
    (inbox.audit_findings?.count || 0);

  return (
    <div data-testid="external-accountant-workspace" className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-violet-600" />
            <h1 className="text-2xl font-bold text-slate-900">External Accountant Workspace</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Command Center للمحاسب القانوني والمراجع الخارجي · KPIs حيّة + صندوق الواردات + 11 قسم
          </p>
        </div>
        <button
          data-testid="accountant-workspace-refresh"
          onClick={() => load(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          تحديث
        </button>
      </div>

      {/* Compliance KPI Bar */}
      <div data-testid="kpi-bar" className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
        <ScoreGauge score={kpi.compliance_score} />
        <KpiTile testid="kpi-vat-status" label="حالة VAT"
                 value={<StatusPill status={kpi.vat_status} />}
                 sub={kpi.last_filing_date ? `آخر تقديم: ${kpi.last_filing_date.slice(0, 10)}` : kpi.vat_status_label} />
        <KpiTile testid="kpi-period-status" label="الفترة الحالية"
                 value={kpi.current_period_name}
                 sub={<StatusPill status={kpi.current_period_status} />} />
        <KpiTile testid="kpi-findings" label="ملاحظات التدقيق"
                 value={kpi.open_audit_findings}
                 sub="مفتوحة بحاجة لمعالجة" />
        <KpiTile testid="kpi-unrec" label="حسابات غير مُسوّاة"
                 value={kpi.unreconciled_accounts}
                 sub="bank reconciliations open" />
        <KpiTile testid="kpi-vat-due" label="ضريبة مستحقة"
                 value={`${fmt(kpi.vat_net_due)} SAR`}
                 sub={kpi.vat_status_label} />
      </div>

      {/* External Accountant Inbox */}
      <section data-testid="accountant-inbox" className="rounded-2xl border border-slate-200 bg-gradient-to-br from-violet-50/50 via-white to-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <InboxIcon className="h-5 w-5 text-violet-600" />
            <h2 className="text-lg font-bold text-slate-900">صندوق وارد المحاسب القانوني</h2>
          </div>
          <span data-testid="inbox-total"
                className={`rounded-full px-3 py-1 text-xs font-bold ${totalInboxCount > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
            {totalInboxCount > 0 ? `${totalInboxCount} عنصر بحاجة انتباه` : "كل الأقسام نظيفة ✓"}
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          «ما الذي يحتاج انتباهي اليوم؟» — يلخص جميع الإشارات الحرجة عبر النظام في مكان واحد.
        </p>

        <div className="mt-4 grid gap-2.5 md:grid-cols-2">
          <InboxRow testid="inbox-vat"     icon={Calculator}   label="مشاكل VAT العالقة"
                    count={inbox.outstanding_vat_issues.count}
                    severity={inbox.outstanding_vat_issues.count ? "warn" : "info"}
                    onClick={() => navigate(inbox.outstanding_vat_issues.link)} />
          <InboxRow testid="inbox-docs"    icon={FolderArchive} label="مستندات مفقودة"
                    count={inbox.missing_documents.count}
                    severity={inbox.missing_documents.count ? "warn" : "info"}
                    onClick={() => navigate(inbox.missing_documents.link)} />
          <InboxRow testid="inbox-unposted" icon={BookOpen}    label="قيود غير مُرحّلة"
                    count={inbox.unposted_journals.count}
                    severity={inbox.unposted_journals.count ? "danger" : "info"}
                    onClick={() => navigate(inbox.unposted_journals.link)} />
          <InboxRow testid="inbox-unrec"   icon={Scale}        label="حسابات بنكية غير مُسوّاة"
                    count={inbox.unreconciled_accounts.count}
                    severity={inbox.unreconciled_accounts.count ? "warn" : "info"}
                    onClick={() => navigate(inbox.unreconciled_accounts.link)} />
          <InboxRow testid="inbox-month-end" icon={Lock}       label="تحذيرات إقفال الشهر"
                    count={inbox.month_end_warnings.count}
                    severity={inbox.month_end_warnings.count ? "warn" : "info"}
                    onClick={() => navigate(inbox.month_end_warnings.link)} />
          <InboxRow testid="inbox-findings" icon={AlertTriangle} label="ملاحظات تدقيق مفتوحة"
                    count={inbox.audit_findings.count}
                    severity={inbox.audit_findings.count > 10 ? "danger" : "warn"}
                    onClick={() => navigate(inbox.audit_findings.link)} />
        </div>
      </section>

      {/* 11 Module Cards */}
      <section>
        <h2 className="mb-3 text-lg font-bold text-slate-900">أقسام الـ Workspace</h2>
        <div data-testid="module-cards-grid" className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {MODULE_DEFS.map((m) => (
            <ModuleCard
              key={m.key}
              testid={`module-card-${m.key}`}
              icon={m.icon}
              title={m.title}
              kpi={cards[m.key]?.kpi || "—"}
              subtitle={cards[m.key]?.subtitle || ""}
              link={m.link}
              color={m.color}
            />
          ))}
        </div>
      </section>

      {/* Permissions footer */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500">
        <span className="font-semibold text-slate-600">Permissions Layer:</span>{" "}
        {(data.permissions || []).join(" · ")}
        <span className="mr-2 text-slate-400">— حالياً Admin فقط، مهيأ للتوسع لاحقاً لـ External Auditor / Tax Authority / Zakat Auditor.</span>
      </div>
    </div>
  );
}
